import React, { useCallback, useContext, useEffect, useRef } from "react";
import { ReactFlowProvider } from "reactflow";
import "reactflow/dist/style.css";
import { Box } from "@mui/material";
import LeftSidebar from "../../../../app/components/flow/designer/leftSidebar/LeftSidebar";
import sxClasses from "./flowdesigner.styles";
import ProgressViewWrapper from "./progress/progress-view-wrapper";
import { useLocation, useParams } from "react-router";
import MetaObjectConverter from "core/services/metaStoreService/metaobject-converter";
import Topbar from "./topbar/topbar";
import { SidebarWrapperRef } from "./sidebar-wrappers/component-form/sidebar-wrapper";
import {
    promptIfUnsavedChanged,
    promptIfUnsavedChangedForStatus
} from "./sidebar-wrappers/component-form/sidebar.utils";
import TableProgressFD from "./progress/table-progress-fd";

import PIISidebarsWrapper from "./sidebar-wrappers/pii-sidebars/pii-sidebars";
import { useAppDispatch, useAppSelector } from "../../../stores/redux/hooks";
import { getAppStatus, getIsConversationalMode } from "./helpers/selectors";
import {
    setAppFQNAction,
    setNamespaceAction,
    setCurrentFlowIdAction,
    setShowAppErrorsSidebarAction,
    setIsDiscoverPIISidebarOpenAction,
    setDiscoverPIIDefaultJobAction,
    setIsSentinelLiveMonitorSidebarOpenAction,
    setSentinelLiveMonitorDefaultSentinelAction,
    resetFlowDesignerStateAction,
    setIsConversationalModeAction
} from "../../../stores/redux/features/flowdesigner/flowdesigner.slice";
import { NavbarContext } from "src/modules/frame";
import { setSidebarOpenAction } from "src/stores/redux/features/striim-ai/striim-ai.slice";
import ResizableSidebar from "./sidebar-wrappers/resizable-sidebar";
import { fetchUserPreferences } from "./helpers/flow-data.utils";
import FlowContainer from "./flow/flow-container";
import lineageMetadataManagerProvider from "app/components/flow/designer/lineageMetadata/lineageMetadataManagerProvider";
import StreamPreviewContainer from "./stream-preview/stream-preview-table/stream-preview-container";

export type FlowUrlProps = {
    appName: string;
    flowName?: string;
    nodeName?: string;
    viewName?: string;
    itemName?: string;
};

// Shows Components Sidebar only for Status CREATED, INVALID, DEPLOY_FAILED (and not in conversational mode)
const LeftSidebarWrapper = () => {
    const appStatus = useAppSelector(getAppStatus);
    const isConversationalMode = useAppSelector(getIsConversationalMode);
    if (isConversationalMode) return null;
    if (appStatus !== "CREATED" && appStatus !== "INVALID" && appStatus !== "DEPLOY_FAILED") return null;
    return (
        <Box sx={sxClasses.leftSidebarWrapper} data-test-id="left-panel">
            <LeftSidebar />
        </Box>
    );
};

const FlowContent = () => {
    const { appName, flowName, viewName, itemName } = useParams<FlowUrlProps>();
    const location = useLocation();

    const sidebarRef = useRef<SidebarWrapperRef>(null);
    const appSettingsRef = useRef<any>(null);
    const dispatch = useAppDispatch();
    const { setIsConversationalMode: setFrameConversationalMode } = useContext(NavbarContext)!;

    const isExceptionView = viewName === "exceptions";

    // 1. Set appFQN, namespace, currentFlowId to store
    // 2. Open App errors sidebar if exception view
    // 3. Fetch user preferences
    useEffect(() => {
        const appFQN = MetaObjectConverter.convertShortNameToFullName(appName, "APPLICATION");
        const nsName = MetaObjectConverter.getNamespace(appName);
        const flowId = flowName
            ? MetaObjectConverter.convertShortNameToFullName(`${nsName}.${flowName}`, "FLOW")
            : appFQN;

        lineageMetadataManagerProvider.init();
        fetchUserPreferences(appFQN, dispatch);

        dispatch(setAppFQNAction(appFQN));
        dispatch(setNamespaceAction(nsName));
        dispatch(setCurrentFlowIdAction(flowId));

        return () => {
            dispatch(resetFlowDesignerStateAction());
        };
    }, [appName, flowName, dispatch]);

    useEffect(() => {
        if (isExceptionView) {
            dispatch(setShowAppErrorsSidebarAction(true));
        } else if (viewName === "sherlock-ai") {
            dispatch(setDiscoverPIIDefaultJobAction(itemName ?? null));
            dispatch(setIsDiscoverPIISidebarOpenAction(true));
        } else if (viewName === "sentinel-ai-activity") {
            dispatch(setSentinelLiveMonitorDefaultSentinelAction(itemName ?? null));
            dispatch(setIsSentinelLiveMonitorSidebarOpenAction(true));
        } else {
            dispatch(setShowAppErrorsSidebarAction(false));
            dispatch(setIsDiscoverPIISidebarOpenAction(false));
            dispatch(setIsSentinelLiveMonitorSidebarOpenAction(false));
            setFrameConversationalMode(false);
        }
    }, [isExceptionView, viewName, itemName, dispatch]);

    // Activate conversational mode when ?mode=conversational is in the URL
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("mode") === "conversational") {
            dispatch(setIsConversationalModeAction(true));
            setFrameConversationalMode(true);
            dispatch(setSidebarOpenAction(true));
        }
    }, []);

    // Prompts the user to confirm discarding unsaved changes
    const handlePromptIfUnsavedChanged = useCallback(
        () => promptIfUnsavedChanged(sidebarRef, appSettingsRef, dispatch, appName, flowName),
        [dispatch, appName, flowName]
    );

    // Alerts the user that unsaved changes must be resolved before a status change
    const handlePromptIfUnsavedChangedForStatus = useCallback(
        () => promptIfUnsavedChangedForStatus(sidebarRef, appSettingsRef),
        []
    );

    return (
        <Box sx={sxClasses.root}>
            <Topbar
                appName={appName}
                flowName={flowName}
                promptIfUnsavedChanged={handlePromptIfUnsavedChanged}
                promptIfUnsavedChangedForStatus={handlePromptIfUnsavedChangedForStatus}
            />

            <Box sx={{ borderBottom: "1px solid #e0e0e0" }}>
                {/* Progress view lite */}
                <ProgressViewWrapper />
            </Box>

            <Box sx={sxClasses.container}>
                {/* Components Sidebar */}
                <LeftSidebarWrapper />
                {/* Main Flow */}
                <FlowContainer promptIfUnsavedChanged={handlePromptIfUnsavedChanged} />
                {/* Right Sidebar */}
                <ResizableSidebar
                    promptIfUnsavedChanged={handlePromptIfUnsavedChanged}
                    sidebarRef={sidebarRef}
                    appSettingsRef={appSettingsRef}
                />
                {/* PII Sidebars */}
                <PIISidebarsWrapper appName={appName} />
                {/* Application Progress modal - mounted on demand so polling only starts when modal is opened */}
                <TableProgressFD />
            </Box>
            <StreamPreviewContainer />
        </Box>
    );
};

const FlowDesignerV2 = () => {
    const { flowName } = useParams<FlowUrlProps>();
    return (
        <ReactFlowProvider key={flowName}>
            <FlowContent />
        </ReactFlowProvider>
    );
};

export default FlowDesignerV2;
